import {assignable} from '../lib/staff';
import {RecordHistory} from './OperationsPages';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import NotesPanel from './NotesPanel';
import { supabase } from '../supabase';

const COLUMNS = [
  { key: 'To Do', label: 'To Do', color: 'var(--text3)' },
  { key: 'In Progress', label: 'In Progress', color: 'var(--blue)' },
  { key: 'Waiting', label: 'Waiting', color: 'var(--yellow)' },
  { key: 'Done', label: 'Done', color: 'var(--green)' },
  { key: 'Cancelled', label: 'Cancelled', color: 'var(--red)' },
];

const ACTIVE_COLUMNS = COLUMNS.filter(c => c.key !== 'Cancelled');

const PRIORITIES = ['High', 'Medium', 'Low'];
const PRIORITY_STYLE = {
  High: { color: 'var(--red)', bg: 'var(--red-dim)' },
  Medium: { color: 'var(--yellow)', bg: 'var(--yellow-dim)' },
  Low: { color: 'var(--text3)', bg: 'var(--bg3)' },
};

function fmtDate(d) {
  if (!d) return null;
  const date = new Date(d + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  const label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  if (diff < 0) return { color: 'var(--red)', text: `${label} (overdue)` };
  if (diff === 0) return { color: 'var(--orange)', text: 'Today' };
  if (diff === 1) return { color: 'var(--yellow)', text: 'Tomorrow' };
  return { color: 'var(--text3)', text: label };
}

// Project search input component
function ProjectSearch({ value, onChange, projects }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = projects.find(p => p.id === value);

  const results = query.length > 0
    ? projects.filter(p => `${p.project_code} ${p.project_name}`.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--accent-dim)', borderRadius: 'var(--radius)', padding: '8px 12px', border: '1px solid var(--border2)' }}>
          <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{selected.project_code}</span>
          <span style={{ fontSize: 12, color: 'var(--text2)', flex: 1 }}>{selected.project_name?.slice(0, 35)}</span>
          <button onClick={() => { onChange(''); setQuery(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      ) : (
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search project code or name..."
        />
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow2)', maxHeight: 220, overflowY: 'auto', marginTop: 4
        }}>
          {results.map(p => (
            <div
              key={p.id}
              onMouseDown={() => { onChange(p.id); setQuery(''); setOpen(false); }}
              style={{ display: 'flex', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>{p.project_code}</span>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{p.project_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskModal({ task, projects, profiles, user, onClose, onSaved, settingsTags, initialStatus = 'To Do' }) {
  const canManage=['owner','operations'].includes(user?.app_metadata?.role);
  const canSetDueDate=canManage||(user?.app_metadata?.role==='business_development'&&(!task||task.created_by===user.id));
  const [title, setTitle] = useState(task?.title || '');
  const [notes, setNotes] = useState(task?.notes || '');
  const [expectedOutput,setExpectedOutput]=useState(task?.expected_output||'');
  const [status, setStatus] = useState(task?.status || initialStatus);
  const [priority, setPriority] = useState(task?.priority || 'Medium');
  const [projectId, setProjectId] = useState(task?.project_id || '');
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to || (user?.app_metadata?.role==='business_development'?user.id:''));
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [tags, setTags] = useState(task?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [visibleTo, setVisibleTo] = useState(
    task?.visible_to ? (Array.isArray(task.visible_to) ? task.visible_to : ['all']) : (canManage?['all']:[user.id])
  );
  const [saving, setSaving] = useState(false);

  const toggleVisible = (id) => {
    if (id === 'all') {
      setVisibleTo(['all']);
      return;
    }
    setVisibleTo(prev => {
      const without = prev.filter(x => x !== 'all');
      if (without.includes(id)) return without.filter(x => x !== id).length === 0 ? ['all'] : without.filter(x => x !== id);
      return [...without, id];
    });
  };

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Unknown';
    const assignedProfile = profiles.find(p => p.id === assignedTo);
    const payload = {
      title: title.trim(), notes: notes || null, expected_output:expectedOutput, status, priority,
      project_id: projectId || null,
      assigned_to: assignedTo || null,
      assigned_name: assignedProfile?.full_name || assignedProfile?.email?.split('@')[0] || null,
      due_date: dueDate || null,
      visible_to: visibleTo,
      tags: tags,
      updated_at: new Date().toISOString(),
    };
    if(!task&&!canManage){payload.assigned_to=user.id;payload.assigned_name=userName;payload.visible_to=[user.id];}
    if(task&&!canManage){for(const key of ['assigned_to','assigned_name','visible_to','project_id','priority','expected_output'])delete payload[key];}
    if(!canSetDueDate) delete payload.due_date;
    try {
      const result = task
        ? await supabase.from('tasks').update(payload).eq('id', task.id).eq('version',task.version).select('id').single()
        : await supabase.from('tasks').insert({ ...payload, created_by: user?.id, created_by_name: userName }).select('id').single();
      if (result.error || !result.data?.id) throw new Error('Save not confirmed');
      onSaved();
    } catch {
      window.alert('The save could not be confirmed. Your draft is still here. Check the task list before retrying.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div><h2>{task ? 'Edit Task' : 'New Task'}</h2>{task&&<RecordHistory table="tasks" id={task.id}/>}</div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <label>Expected output<textarea value={expectedOutput} disabled={!(['owner','operations'].includes(user.app_metadata?.role))} onChange={e=>setExpectedOutput(e.target.value)} placeholder="What should be delivered?" /></label>
          <div className="form-row">
            <div>
              <label>Title *</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?" autoFocus />
            </div>
          </div>
          <div className="form-row cols-3">
            <div>
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {COLUMNS.map(c => <option key={c.key}>{c.key}</option>)}
              </select>
            </div>
            <div>
              <label>Priority</label>
              <select disabled={!canManage} value={priority} onChange={e => setPriority(e.target.value)}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label>Due Date</label>
              <input disabled={!canSetDueDate} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Tags</label>
              {/* Preset tags from settings */}
              {settingsTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {settingsTags.map(tag => {
                    const selected = tags.includes(tag);
                    return (
                      <button key={tag} type="button" onClick={() => setTags(t => selected ? t.filter(x => x !== tag) : [...t, tag])}
                        style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                          background: selected ? 'var(--accent)' : 'var(--bg3)',
                          color: selected ? '#fff' : 'var(--text2)',
                          border: selected ? 'none' : '1px solid var(--border2)' }}
                      >{tag}</button>
                    );
                  })}
                </div>
              )}
              {/* Selected tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {tags.filter(t => !settingsTags.includes(t)).map(tag => (
                  <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--blue-dim)', color: 'var(--blue)', fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                    {tag}
                    <button onClick={() => setTags(t => t.filter(x => x !== tag))} style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newTag} onChange={e => setNewTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newTag.trim()) { if (!tags.includes(newTag.trim())) setTags(t => [...t, newTag.trim()]); setNewTag(''); e.preventDefault(); }}}
                  placeholder="Or type a custom tag..." />
                <button className="btn-ghost" onClick={() => { if (newTag.trim() && !tags.includes(newTag.trim())) { setTags(t => [...t, newTag.trim()]); setNewTag(''); }}}>+ Add</button>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Assign To</label>
              <select disabled={!canManage} value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">— Unassigned —</option>
                {profiles.filter(p=>assignable(p)||p.id===assignedTo).map(p => (
                  <option key={p.id} value={p.id} disabled={!assignable(p)}>{p.full_name || p.email?.split('@')[0]}{!assignable(p)?' · Pasif':''}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Visible To</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  disabled={!canManage}
                  onClick={() => toggleVisible('all')}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit', border: '1px solid var(--border2)',
                    background: visibleTo.includes('all') ? 'var(--accent)' : 'transparent',
                    color: visibleTo.includes('all') ? '#fff' : 'var(--text2)',
                  }}
                >All team</button>
                {profiles.map(p => {
                  const name = p.full_name || p.email?.split('@')[0];
                  const selected = !visibleTo.includes('all') && visibleTo.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={!canManage}
                      onClick={() => toggleVisible(p.id)}
                      style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', border: '1px solid var(--border2)',
                        background: selected ? 'var(--blue)' : 'transparent',
                        color: selected ? '#fff' : 'var(--text2)',
                      }}
                    >{name}</button>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                {visibleTo.includes('all') ? 'Operations can see all tasks; other members see assigned or explicitly shared tasks.' : `Visible to operations + ${visibleTo.length} selected person(s).`}
              </p>
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Linked Project</label>
              <ProjectSearch value={projectId} onChange={setProjectId} projects={projects} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Description</label>
              <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Task description..." />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving || !title.trim()}>
            {saving ? 'Saving...' : task ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StatusMenu({task, onChange, disabled, columns=COLUMNS}) {
  const [position, setPosition] = useState(null);
  const trigger = useRef(null), menu = useRef(null);
  const close = () => {setPosition(null); trigger.current?.focus();};
  useEffect(() => {
    if (!position) return;
    menu.current?.querySelector('[aria-checked="true"]')?.focus();
    const outside = e => {if (!menu.current?.contains(e.target) && !trigger.current?.contains(e.target)) setPosition(null);};
    const dismiss = () => setPosition(null);
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {document.removeEventListener('pointerdown', outside); window.removeEventListener('resize', dismiss); window.removeEventListener('scroll', dismiss, true);};
  }, [position]);
  return <>
    <button ref={trigger} className="kanban-status" data-status={task.status} disabled={disabled} aria-haspopup="menu" aria-expanded={!!position} aria-label={`Change status: ${task.title}`} onClick={() => {
      if(position) {close(); return;}
      const r=trigger.current.getBoundingClientRect();
      setPosition({left:Math.max(8,Math.min(r.left,window.innerWidth-220)), top:Math.max(8,Math.min(r.bottom+6,window.innerHeight-250))});
    }}><i/>{columns.find(c=>c.key===task.status)?.label||task.status}<span>⌄</span></button>
    {position && createPortal(<div ref={menu} className="kanban-status-menu" role="menu" aria-label="Status" style={position} onKeyDown={e => {
      if(e.key==='Escape'){e.preventDefault();close();}
      if(e.key==='Tab'){setPosition(null);}
      if(['ArrowDown','ArrowUp','Home','End'].includes(e.key)) {
        e.preventDefault();const items=[...menu.current.querySelectorAll('[role=menuitemradio]')];const index=items.indexOf(document.activeElement);
        items[e.key==='Home'?0:e.key==='End'?items.length-1:(index+(e.key==='ArrowDown'?1:-1)+items.length)%items.length]?.focus();
      }
    }}><div className="kanban-menu-label">Move to</div>{columns.map(c=><button key={c.key} role="menuitemradio" aria-checked={task.status===c.key} data-status={c.key} onClick={()=>{close();onChange(task.id,c.key);}}><i/>{c.label}{task.status===c.key && <span>✓</span>}</button>)}</div>,document.body)}
  </>;
}

function TaskCard({ task, projects, onEdit, onStatusChange, onDetail, isDragging, dragHandlers, cancelled, pending }) {
  const project = projects.find(p => p.id === task.project_id);
  const due = fmtDate(task.due_date);
  const done = task.status === 'Done';
  return <article className={`kanban-card ${done ? 'is-complete' : ''} ${isDragging ? 'is-dragging' : ''}`} data-status={task.status} {...dragHandlers} aria-busy={pending}>
    <button className="kanban-card-open" onClick={()=>onDetail ? onDetail(task) : onEdit(task)}>
      {!done && <div className="kanban-context">{project ? `${project.project_code} / ${project.project_name}` : 'Liongate'}{task.tags?.length ? ` / ${task.tags.join(' · ')}` : ''}</div>}
      <h3>{done && <span className="kanban-done-icon">✓</span>}{task.title}</h3>
      {!done && <>
        {task.notes && <p>{task.notes}</p>}
        <div className="kanban-card-meta"><span className="kanban-owner"><b>{(task.assigned_name || '?').charAt(0).toUpperCase()}</b>{task.assigned_name || 'Unassigned'}</span>{due && <time dateTime={task.due_date} style={{color:due.color}}>▦ {due.text}</time>}</div>
      </>}
    </button>
    {!done && <div className="kanban-card-actions">
      {!cancelled && <button className="kanban-complete" disabled={pending} onClick={()=>onStatusChange(task.id,'Done')} aria-label={`Complete ${task.title}`} title="Mark complete">✓</button>}
      <StatusMenu task={task} onChange={onStatusChange} disabled={pending}/>
      {task.priority === 'High' && <span className="kanban-priority">High priority</span>}
    </div>}
  </article>;
}

export default function TasksPage({ projects, user, isAdmin }) {
  const canCreate=isAdmin||user?.app_metadata?.role==='business_development';
  const [tasks, setTasks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filterAssigned, setFilterAssigned] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [search, setSearch] = useState('');
  const [newTaskStatus, setNewTaskStatus] = useState('To Do');
  const [pendingIds, setPendingIds] = useState(new Set());
  const pendingRef = useRef(new Set());
  const boardRef = useRef(null);
  const openNew = (status = 'To Do') => {setNewTaskStatus(status); setModal('new');};

  const [settingsTags, setSettingsTags] = useState([]);

  useEffect(() => {
    supabase.from('settings').select('value').eq('key', 'task_tags').single().then(({ data }) => {
      if (data?.value) setSettingsTags(data.value);
    });
  }, []);

  const load = async () => {
    const [{ data: t, error: taskError }, { data: p, error: profileError }] = await Promise.all([
      supabase.from('tasks').select('*').eq('workspace','general').order('created_at'),
      supabase.from('profiles').select('*'),
    ]);
    if (taskError || profileError) { setLoading(false); window.alert('Tasks could not be refreshed. Existing information has been kept.'); return; }
    setTasks(t || []);
    setProfiles(p || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    const { data, error } = await supabase.from('tasks').delete().eq('id', id).select('id');
    if (error || !data?.some(row => row.id === id)) { window.alert('Deletion could not be confirmed. Refresh before retrying.'); return; }
    load();
  };

  const changeStatus = async (id, status) => {
    if(pendingRef.current.has(id)) return;
    const current=tasks.find(t=>t.id===id);
    if(!current || current.status===status) return;
    pendingRef.current.add(id);setPendingIds(new Set(pendingRef.current));
    try {
      let query=supabase.from('tasks').update({status,updated_at:new Date().toISOString()}).eq('id',id);
      query=current.updated_at ? query.eq('updated_at',current.updated_at) : query.is('updated_at',null);
      const {data,error}=await query.select('*').single();
      if(error || !data) {window.alert('This task may have changed elsewhere, or the update failed. Refreshing the board; please review before trying again.');await load();return;}
      setTasks(t=>t.map(x=>x.id===id?data:x));
      setDetailTask(t=>t?.id===id?data:t);
    } catch {window.alert('The change could not be confirmed. Please refresh before retrying.');}
    finally {pendingRef.current.delete(id);setPendingIds(new Set(pendingRef.current));}
  };

  const handleDragStart = (e, task) => {e.dataTransfer.setData('text/plain',task.id); e.dataTransfer.effectAllowed='move';setDragging(task);};
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };
  const handleDragOver = (e, colKey) => { e.preventDefault(); setDragOver(colKey);
    const board=boardRef.current;if(board){const r=board.getBoundingClientRect();if(e.clientX>r.right-70)board.scrollLeft+=24;else if(e.clientX<r.left+70)board.scrollLeft-=24;}
  };
  const handleDrop = async (e, colKey) => {
    e.preventDefault();
    if (!dragging || dragging.status === colKey) { setDragging(null); setDragOver(null); return; }
    await changeStatus(dragging.id, colKey);
    setDragging(null); setDragOver(null);
  };

  const filtered = tasks.filter(t => {
    if (t.status === 'Cancelled') return false;
    // Non-admins: check visibility
    if (!isAdmin) {
      const vt = Array.isArray(t.visible_to) ? t.visible_to : ['all'];
      const canSee = vt.includes('all') || vt.includes(user?.id) || t.assigned_to === user?.id || t.created_by === user?.id;
      if (!canSee) return false;
    }
    const matchAssigned = !filterAssigned || t.assigned_to === filterAssigned;
    const matchPriority = !filterPriority || t.priority === filterPriority;
    const matchTag = !filterTag || (t.tags && t.tags.includes(filterTag));
    const project=projects.find(p=>p.id===t.project_id);
    const matchSearch=`${t.title} ${t.notes||''} ${t.assigned_name||''} ${(t.tags||[]).join(' ')} ${project?.project_code||''} ${project?.project_name||''}`.toLowerCase().includes(search.toLowerCase().trim());
    return matchAssigned && matchPriority && matchTag && matchSearch;
  });

  const cancelled = tasks.filter(t => {
    if (t.status !== 'Cancelled') return false;
    if (!isAdmin) {
      const vt = Array.isArray(t.visible_to) ? t.visible_to : ['all'];
      const canSee = vt.includes('all') || vt.includes(user?.id) || t.assigned_to === user?.id || t.created_by === user?.id;
      if (!canSee) return false;
    }
    return true;
  });
  const allTags = [...new Set(tasks.flatMap(t => t.tags || []))].sort();
  const grouped = {};
  ACTIVE_COLUMNS.forEach(c => { grouped[c.key] = filtered.filter(t => t.status === c.key); });

  return (
    <div className="kanban-page">
      <div className="kanban-heading">
        <div>
          <h1>Tasks</h1><p>Genel görevler · Proje teslim işleri için Aktif İşler sekmesini kullan.</p>
          <p>{tasks.filter(t => !['Cancelled','Done'].includes(t.status)).length} active · {cancelled.length} cancelled</p>
        </div>
        <button className="btn-primary" disabled={!canCreate} onClick={() => openNew()}>+ New Task</button>
      </div>

      <div className="crm-task-filters" style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {isAdmin && (
          <select aria-label="Filter by team member" value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)} style={{ width: 180 }}>
            <option value="">All team members</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name || p.email?.split('@')[0]}</option>)}
          </select>
        )}
        <select aria-label="Filter by priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ width: 150 }}>
          <option value="">All priorities</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </select>
        <select aria-label="Filter by tag" value={filterTag} onChange={e => setFilterTag(e.target.value)} style={{ width: 150 }}>
          <option value="">All tags</option>
          {allTags.map(tag => <option key={tag}>{tag}</option>)}
        </select>
      </div>

      <div className="kanban-toolbar"><span className="kanban-view">▦ Board <small>{filtered.length}</small></span><input aria-label="Search tasks" placeholder="Search tasks…" value={search} onChange={e=>setSearch(e.target.value)}/><button className="kanban-refresh" onClick={load} aria-label="Refresh tasks" title="Refresh tasks">↻</button></div>
      <div className="kanban-navigation"><span>Drag to move · Open a card for details</span><div><button aria-label="Scroll board left" onClick={()=>boardRef.current?.scrollBy({left:-320,behavior:'smooth'})}>←</button><button aria-label="Scroll board right" onClick={()=>boardRef.current?.scrollBy({left:320,behavior:'smooth'})}>→</button><button onClick={()=>boardRef.current?.scrollTo({left:boardRef.current.scrollWidth,behavior:'smooth'})}>Go to completed</button></div></div>
      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          {/* Active columns */}
          <div className="kanban-board" ref={boardRef}>
            {ACTIVE_COLUMNS.map(col => (
              <div
                key={col.key} className={`kanban-column ${dragOver===col.key ? 'is-drop-target' : ''}`} data-status={col.key}
                onDragOver={e => handleDragOver(e, col.key)} onDrop={e => handleDrop(e, col.key)}
              >
                <div className="kanban-column-title"><span className="crm-stage">{col.label}</span><small>{grouped[col.key]?.length || 0}</small><button aria-label={`Add task to ${col.label}`} disabled={!canCreate} onClick={()=>openNew(col.key)}>+</button></div>

                <div className="kanban-card-list">
                  {grouped[col.key]?.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      projects={projects}
                      onEdit={t => setModal(t)}
                      onDelete={deleteTask}
                      onStatusChange={changeStatus}
                      onDetail={t => setDetailTask(t)}
                      pending={pendingIds.has(task.id)}
                      isDragging={dragging?.id === task.id}
                      cancelled={false}
                      dragHandlers={{
                        draggable: !pendingIds.has(task.id),
                        onDragStart: e => handleDragStart(e, task),
                        onDragEnd: handleDragEnd,
                      }}
                    />
                  ))}
                  {grouped[col.key]?.length === 0 && (
                    <div className="kanban-empty">No tasks here</div>
                  )}
                </div>


              </div>
            ))}
          </div>

          {/* Cancelled section */}
          <div className="kanban-archive"
            onDragOver={e => handleDragOver(e, 'Cancelled')}
            onDrop={e => handleDrop(e, 'Cancelled')}
            style={{
              background: dragOver === 'Cancelled' ? 'var(--red-dim)' : 'var(--bg3)',
              borderRadius: 'var(--radius2)', padding: '14px 16px',
              border: dragOver === 'Cancelled' ? '2px solid var(--red)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            <div role="button" tabIndex={0} aria-expanded={showCancelled} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setShowCancelled(v=>!v);}}}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              onClick={() => setShowCancelled(v => !v)}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cancelled</span>
              <span style={{ fontSize: 11, background: 'var(--bg2)', color: 'var(--text3)', padding: '1px 7px', borderRadius: 10, border: '1px solid var(--border)' }}>{cancelled.length}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>{showCancelled ? '▲ Hide' : '▼ Show'}</span>
            </div>

            {showCancelled && cancelled.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 14 }}>
                {cancelled.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    projects={projects}
                    onEdit={t => setModal(t)}
                    onDelete={deleteTask}
                    onStatusChange={changeStatus}
                    isDragging={false}
                    cancelled={true}
                    dragHandlers={{}}
                  />
                ))}
              </div>
            )}
            {showCancelled && cancelled.length === 0 && (
              <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 12 }}>No cancelled tasks.</p>
            )}
          </div>
        </>
      )}

      {detailTask && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailTask(null)}>
          <div className="modal" style={{ maxWidth: 580 }}>
            <div className="modal-header">
              <div>
                <h2 style={{ fontSize: 15 }}>{detailTask.title}</h2>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700,
                    background: PRIORITY_STYLE[detailTask.priority]?.bg,
                    color: PRIORITY_STYLE[detailTask.priority]?.color,
                    padding: '2px 8px', borderRadius: 10 }}>{detailTask.priority}</span>
                  {detailTask.assigned_name && <span style={{ fontSize: 11, color: 'var(--text3)' }}>👤 {detailTask.assigned_name}</span>}
                  {detailTask.due_date && <span style={{ fontSize: 11, color: 'var(--text3)' }}>📅 {new Date(detailTask.due_date).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => { setModal(detailTask); setDetailTask(null); }}>Edit</button>
                <button className="close-btn" onClick={() => setDetailTask(null)}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="kanban-detail-actions"><StatusMenu task={detailTask} onChange={changeStatus} disabled={pendingIds.has(detailTask.id)}/>{detailTask.status !== 'Done' && <button className="btn-ghost" disabled={pendingIds.has(detailTask.id)} onClick={()=>changeStatus(detailTask.id,'Done')}>✓ Mark complete</button>}<button className="btn-danger" onClick={async()=>{await deleteTask(detailTask.id);setDetailTask(null);}}>Delete</button></div>
              {detailTask.notes && (
                <div style={{ marginBottom: 20, background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Description</div>
                  <p style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{detailTask.notes}</p>
                </div>
              )}
              <NotesPanel taskId={detailTask.id} user={user} />
            </div>
          </div>
        </div>
      )}

      {modal && (
        <TaskModal
          initialStatus={newTaskStatus}
          task={modal === 'new' ? null : modal}
          projects={projects}
          profiles={profiles}
          user={user}
          settingsTags={settingsTags}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
